import { bootcampOverviewModuleGroq, BootcampOverviewUserData, BootcampPages } from '~/types/bootcamp'

const BOSS_PERCENTAGE = 10 // Allocated percentage for boss in total progress

/**
 * Count total chests in a Bootcamp
 *
 * @param bootcampPages Chests data of Bootcamp Levels
 * @returns {number} Number of total chests
 */
const countTotalChestsInBootcamp = (bootcampPages: BootcampPages): number => {
  const totalChests =
    bootcampPages?.reduce<number>((activitiesAccumulator, page) => {
      const activities =
        page?.activities?.reduce<number>((difficultiesAccumulator, level) => {
          const difficulties =
            level?.difficulties?.reduce<number>((chestAccumulator, chest) => {
              return chestAccumulator + (chest ?? 0)
            }, 0) ?? 0
          return difficultiesAccumulator + difficulties
        }, 0) ?? 0
      return activitiesAccumulator + activities
    }, 0) ?? 0

  return totalChests
}

/**
 * Calculates overall user progress of a Bootcamp
 * @param bootcampLevelsData Sanity data of Bootcamp levels
 * @param userBootcampData User data from db/firestore
 *
 * @returns {Object} progressData
 * @returns {number} progressData.progress - User Bootcamp progress in percentage
 * @returns {boolean} progressData.hasDefeatedBoss - User has defeated the Bootcamp boss or not
 * @returns {number} progressData.totalChests - Total chests available in a Bootcamp
 * @returns {number} progressData.earnedChests - Total earned chests from the Bootcamp
 */
export const getBootcampProgress = (
  bootcampData: Pick<bootcampOverviewModuleGroq, 'chests' | 'moduleNumber'>,
  userBootcampData?: BootcampOverviewUserData[],
): {
  progress: number
  hasDefeatedBoss: boolean
  totalChests: number
  earnedChests: number
} => {
  const totalChests = countTotalChestsInBootcamp(bootcampData?.chests)

  const userProgressData = userBootcampData?.find((x) => x.moduleId === bootcampData.moduleNumber)

  const earnedChests = Math.min(userProgressData?.chests ?? 0, totalChests)

  const hasDefeatedBoss = userProgressData?.bossDefeated ?? false

  const progress =
    totalChests > 0
      ? (earnedChests / totalChests) * (100 - BOSS_PERCENTAGE) + (hasDefeatedBoss ? BOSS_PERCENTAGE : 0)
      : 0

  return {
    progress,
    hasDefeatedBoss,
    totalChests,
    earnedChests,
  }
}
